/* eslint-disable @typescript-eslint/no-explicit-any */
import { IAdserverCampaign } from '~/src/dash/api/campaign/campaign';
import { IHeaderBidderParamOption, IPublisher } from '~/src/dash/api/console-api';
import { getResellerTitle } from '~/src/dash/resellers/utils/importResellerConfig';
import { getHeaderBidderAdaptersForPublisher } from '~/src/dash/utils/header-bidder-adapters';
import { PID } from '~/src/dash/utils/publishers';

interface IKeyValPair {
  key: string;
  text: string;
}

export const ENDPOINT_STAGING_KEY = 'Staging';
export const ENDPOINT_PROD_KEY = 'Production';
export const ENDPOINT_DEFAULT_KEY = 'Default';
export const ENDPOINT_EU_KEY = 'EU';
export const ENDPOINT_APAC_KEY = 'APAC';
export const ENDPOINT_US_WEST_KEY = 'US_WEST';
export const ENDPOINT_APAC_NORTH_KEY = 'APAC_NORTH';

export const NATIVE_VAST_AD_FORMAT_BANNER = 'Banner';
export const NATIVE_VAST_AD_FORMAT_CLICKABLE_VIDEO = 'ClickableVideo';
export const NATIVE_VAST_AD_FORMAT_BANNER_VIDEO_OVERLAY = 'BannerVideoOverlay';
export const NATIVE_VAST_AD_FORMAT_COMPANION = 'Companion';
export const NATIVE_VAST_AD_FORMAT_HALO_BANNER = 'HaloBanner';

function handleEmptyParamValueError(messageIfEmpty: string): (v: string) => string {
  return function (v: string): string {
    if (!v || v === '') {
      return messageIfEmpty;
    }
    return '';
  };
}

// getAmazonORTBEndpointDomainOptions returns options for endpoint domains given a publisher
const getAmazonORTBEndpointDomainOptions = (
  publisherID: string | undefined,
): Record<string, string | Object>[] => {
  const options = [
    {
      key: '1',
      text: 'Samsung NA',
      isEnabled: (publisherID: string | undefined): boolean => publisherID === PID.samsungNA,
    },
    {
      key: '2',
      text: 'Samsung UK',
      isEnabled: (publisherID: string | undefined): boolean => publisherID === PID.samsungEU,
    },
    {
      key: '3',
      text: 'Default (NA)',
      isEnabled: (publisherID: string | undefined): boolean => !!publisherID,
    },
    {
      key: '4',
      text: 'Default (EU)',
      isEnabled: (publisherID: string | undefined): boolean => !!publisherID,
    },
  ];

  return options.filter((option) => option.isEnabled(publisherID));
};

// rename bidderparam name, description, text etc. for display on UI
export const getHeaderBidderParamsUIConfig = (publisherID: string): Record<string, any> => {
  return {
    acuity: {
      publisherid: {
        rename: 'Publisher ID',
        renameDescription: (): string => {
          return 'pub_id';
        },
      },
      endpointDomain: {
        type: 'enum',
        baseValue: '0',
        items: [
          {
            key: '0',
            text: 'US',
          },
          {
            key: '1',
            text: 'EMEA',
          },
        ],
        rename: 'Endpoint Domain',
        renameDescription: (): string => {
          return 'Example: US East or EMEA';
        },
        errorHandler: handleEmptyParamValueError('Endpoint Domain is required.'),
      },
    },
    admixer: {
      uuid: {
        rename: 'UUID',
        renameDescription: (): string => {
          return 'uuid';
        },
        errorHandler: handleEmptyParamValueError('UUID is required.'),
      },
      zone: {
        rename: 'Zone',
        renameDescription: (): string => {
          return 'zone';
        },
        errorHandler: handleEmptyParamValueError('Zone is required.'),
      },
    },
    amazonORTB: {
      publisherID: {
        rename: 'Publisher ID',
        errorHandler: handleEmptyParamValueError('Publisher ID is required.'),
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
      },
      endpointDomain: {
        type: 'enum',
        baseValue: '3',
        items: getAmazonORTBEndpointDomainOptions(publisherID),
        rename: 'Endpoint Domain',
        renameDescription: (): string => {
          return '';
        },
        errorHandler: handleEmptyParamValueError('Endpoint Domain is required.'),
      },
    },
    appnexus: {
      endpointDomain: {
        type: 'enum',
        baseValue: 'publica-useast.adnxs.com',
        items: [
          {
            key: 'publica-useast.adnxs.com',
            text: 'US',
          },
          {
            key: 'publica-emea.adnxs.com',
            text: 'EMEA',
          },
          {
            key: 'publica-apac.adnxs.com',
            text: 'APAC',
          },
        ],
        rename: 'Endpoint Domain',
        renameDescription: (): string => {
          return 'Example: US, EMEA, APAC';
        },
        errorHandler: handleEmptyParamValueError('Endpoint Domain is required.'),
      },
      bidFloorAdjustment: {
        rename: 'Bid Floor Adjustment',
      },
      member: {
        rename: 'Member',
      },
      placementId: {
        rename: 'Placement ID',
        renameDescription: (): string => {
          return 'placementId';
        },
      },
      tag_id: {
        rename: 'Tag ID',
        renameDescription: (): string => {
          return 'tag_id - Optional';
        },
      },
      publisher_id: {
        rename: 'Publisher ID',
        renameDescription: (): string => {
          return 'publisher_id';
        },
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string =>
          'Precise acceptable durations for video creatives in seconds - comma separated integers',
      },
    },
    beachfront: {
      appId: {
        rename: 'app_id (web) exchange_id (OTT)',
      },
      bidfloor: {
        hidden: true,
        rename: 'Bid Floor',
      },
      endpointDomain: {
        rename: 'Endpoint Domain',
        renameDescription: (description: string): string => {
          return '(endpointDomain) ' + description;
        },
      },
      publisherID: {
        rename: 'Publisher ID',
        renameDescription: (description: string): string => {
          return '(publisherID) ' + description;
        },
      },
    },
    cadent: {
      region: {
        rename: 'Region',
        type: 'enum',
        items: [
          {
            key: 'US',
            text: 'US',
          },
          {
            key: 'EU',
            text: 'EU',
          },
        ],
      },
      tagID: {
        rename: 'Tag ID',
        renameDescription: (): string => {
          return 'tag_id';
        },
      },
    },
    column6: {
      placement_id: {
        rename: 'Placement ID',
        renameDescription: (): string => 'placement_id',
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string =>
          'Precise acceptable durations for video creatives in seconds - comma separated integers',
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
        renameDescription: (): string =>
          'Price floor for the dynamic portion of a video ad pod; a value of 0 indicates that this value will not be used',
      },
    },
    connatix: {
      tag_id: {
        rename: 'Tag ID',
        errorHandler: handleEmptyParamValueError('Tag ID is required.'),
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string =>
          'Precise acceptable durations for video creatives in seconds - comma separated integers',
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
        renameDescription: (): string =>
          'Price floor for the dynamic portion of a video ad pod; a value of 0 indicates that this value will not be used',
      },
    },
    criteo: {
      publisher_id: {
        rename: 'Publisher ID',
        errorHandler: handleEmptyParamValueError('Publisher ID is required.'),
      },
      placement_id: {
        rename: 'Placement ID',
        renameDescription: (): string => {
          return 'Placement ID ';
        },
      },
      network_id: {
        rename: 'Network ID',
        errorHandler: handleEmptyParamValueError('Network ID is required.'),
      },
      ad_unit_id: {
        rename: 'Ad Unit ID',
      },
      endpoint_domain: {
        type: 'enum',
        baseValue: '1',
        items: [
          {
            key: '1',
            text: 'US EAST',
          },
          {
            key: '2',
            text: 'US WEST/SOUTH',
          },
          {
            key: '3',
            text: 'EMEA',
          },
          {
            key: '4',
            text: 'APAC NORTH',
          },
          {
            key: '5',
            text: 'APAC SOUTH',
          },
        ],
        rename: 'Endpoint Domain',
        renameDescription: (): string => {
          return '';
        },
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
      },
    },
    dynamicGateway: {
      globalTimeoutOffset: {
        onlyForSuperUser: true,
      },
      siteID: {
        rename: 'Channel ID',
        renameDescription: (description: string): string => {
          return '(siteId) ' + description;
        },
      },
      platformMarketplacePublisher: {
        rename: 'Managed Platform Connections Publisher',
        type: 'enum',
      },
    },
    dynamicPlatformConnection: {
      globalTimeoutOffset: {
        onlyForSuperUser: true,
      },
      siteID: {
        rename: 'Channel ID',
        renameDescription: (description: string): string => {
          return '(siteId) ' + description;
        },
      },
      platformMarketplacePublisher: {
        rename: 'Managed Platform Connections Publisher',
        type: 'enum',
      },
    },
    finecast: {
      member_id: {
        rename: 'Member ID',
        errorHandler: handleEmptyParamValueError('Member ID is required.'),
      },
      placement_id: {
        rename: 'DLP Placement ID',
        errorHandler: handleEmptyParamValueError('DLP Placement ID is required.'),
      },
      rid: {
        rename: 'Reach Placement ID',
      },

      endpoint_domain: {
        type: 'enum',
        baseValue: '1',
        items: [
          {
            key: '1',
            text: 'US',
          },
          {
            key: '2',
            text: 'Singapore',
          },
        ],
        rename: 'Endpoint Domain',
        renameDescription: (): string => {
          return '';
        },
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
      },
    },
    freewheel: {
      zoneId: {
        rename: 'Zone ID',
        renameDescription: (description: string): string => {
          return '(zoneId) ' + description;
        },
      },
      endpoint: {
        type: 'enum',
        rename: 'Endpoint',
        baseValue: 'new',
        onlyForSuperUser: true,
        items: [
          {
            key: '',
            text: 'Old Endpoint (wo pub ID) - same as "None"',
          },
          {
            key: 'new',
            text: 'New Endpoint (w pub ID)',
          },
        ],
      },
      publisher_id: {
        rename: 'Publisher ID',
        baseValue: '',
        errorHandler: handleEmptyParamValueError('Please enter a Publisher ID.'),
      },
    },
    freewheelssp: {
      zoneId: {
        rename: 'Zone ID',
        isVisible: (options: IHeaderBidderParamOption[]) => {
          return options.some((opt) => opt.Name === 'ortbType' && opt.Value === 'sfx');
        },
        renameDescription: (description: string): string => {
          return '(zoneId) ' + description;
        },
      },
      prefixId: {
        rename: 'Endpoint Prefix ID',
        baseValue: '',
        errorHandler: handleEmptyParamValueError('Please enter a Prefix ID.'),
      },
      ortbType: {
        type: 'enum',
        rename: 'oRTB Type',
        baseValue: 'sfx',
        items: [
          {
            key: 'sfx',
            text: 'SFX',
          },
          {
            key: 'tvp',
            text: 'TVP',
          },
        ],
      },
      siteSectionId: {
        rename: 'Site Section ID',
        // only for TVP.
        isVisible: (options: IHeaderBidderParamOption[]) => {
          return options.some((opt) => opt.Name === 'ortbType' && opt.Value === 'tvp');
        },
      },
      profileId: {
        rename: 'Profile ID',
        // only for TVP
        isVisible: (options: IHeaderBidderParamOption[]) => {
          return options.some((opt) => opt.Name === 'ortbType' && opt.Value === 'tvp');
        },
      },
      networkId: {
        rename: 'Network ID',
        // only for TVP
        isVisible: (options: IHeaderBidderParamOption[]) => {
          return options.some((opt) => opt.Name === 'ortbType' && opt.Value === 'tvp');
        },
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') return '';
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') return '';
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
      },
    },
    // now Azerion
    improvedigital: {
      exchangetagid: {
        rename: 'Exchange Tag ID',
      },
      partnerid: {
        rename: 'Partner ID',
      },
      endpointDomain: {
        type: 'enum',
        baseValue: '1',
        items: [
          {
            key: '1',
            text: 'Default',
          },
          {
            key: '2',
            text: 'APAC',
          },
          {
            key: '3',
            text: 'EU',
          },
          {
            key: '4',
            text: 'US & LATAM',
          },
        ],
        rename: 'Endpoint Domain',
        errorHandler: handleEmptyParamValueError('Endpoint Domain is required.'),
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string =>
          'Precise acceptable durations for video creatives in seconds - comma separated integers',
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
        renameDescription: (): string =>
          'Price floor for the dynamic portion of a video ad pod; a value of 0 indicates that this value will not be used',
      },
    },
    inmobi: {
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
        renameDescription: (): string =>
          'Price floor for the dynamic portion of a video ad pod; a value of 0 indicates that this value will not be used',
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string =>
          'Precise acceptable durations for video creatives in seconds - comma separated integers',
      },
      tagid: {
        rename: 'Tag ID',
        renameDescription: (): string => {
          return 'tag_id';
        },
      },
    },
    iponweb: {
      tagid: {
        rename: 'Tag ID',
        renameDescription: (): string => {
          return 'tag_id';
        },
      },
      endpointDomain: {
        type: 'enum',
        baseValue: '0',
        items: [
          {
            key: '0',
            text: 'Default',
          },
          {
            key: '1',
            text: 'US East',
          },
          {
            key: '2',
            text: 'US West',
          },
          {
            key: '3',
            text: 'EU',
          },
          {
            key: '4',
            text: 'APAC',
          },
        ],
        rename: 'Endpoint Domain',
        renameDescription: (): string => {
          return 'Example: US East or APAC';
        },
        errorHandler: handleEmptyParamValueError('Endpoint Domain is required.'),
      },
    },
    krush: {
      endpointid: {
        rename: 'Endpoint ID (Key)',
        renameDescription: (): string => {
          return 'endpointid';
        },
      },
    },
    platformConnection: {
      globalTimeoutOffset: {
        onlyForSuperUser: true,
      },
      siteID: {
        rename: 'Channel ID',
        renameDescription: (description: string): string => {
          return '(siteId) ' + description;
        },
      },
      platformMarketplacePublisher: {
        rename: 'Managed Platform Connections Publisher',
        type: 'enum',
      },
    },
    gateway: {
      globalTimeoutOffset: {
        onlyForSuperUser: true,
      },
      siteID: {
        rename: 'Channel ID',
        renameDescription: (description: string): string => {
          return '(siteId) ' + description;
        },
      },
      platformMarketplacePublisher: {
        rename: 'Managed Platform Connections Publisher',
        type: 'enum',
      },
    },
    gatewayTier0: {
      globalTimeoutOffset: {
        onlyForSuperUser: true,
      },
      siteID: {
        rename: 'Channel ID',
        renameDescription: (description: string): string => {
          return '(siteId) ' + description;
        },
      },
      platformMarketplacePublisher: {
        rename: 'Managed Platform Connections Publisher',
        type: 'enum',
      },
    },
    gumgum: {
      tracking_id: {
        rename: 'Tracking ID',
        errorHandler: handleEmptyParamValueError('Tracking ID is required.'),
      },
    },
    madhive: {
      publisher_id: {
        rename: 'Publisher ID',
        renameDescription: (): string => {
          return 'publisher_id';
        },
      },
    },
    magnite: {
      endpointsubdomain: {
        rename: 'Endpoint Subdomain',
        onlyForSuperUser: true,
        defaultValue: 'unity', // change this hardcoded value for future integration of this bidder → should still be 'unity' for unity
      },
      endpointroute: {
        rename: 'Endpoint Route',
        onlyForSuperUser: true,
        defaultValue: 'unity', // change this hardcoded value for future integration of this bidder → should still be 'unity' for unity
      },
      adcode: {
        rename: 'Ad Code',
        hidden: true, // change this value for future integration of this bidder → should stay hidden for unity
      },
    },
    magnitectv: {
      publisher_seat_id: {
        rename: 'Publisher Seat ID',
        renameDescription: (description: string): string => {
          return '(publisherID) ' + description;
        },
      },
      ad_unit_id: {
        rename: 'Ad Unit ID',
      },
      dmp_key: {
        rename: 'DMP Key',
      },
      endpointRegion: {
        type: 'enum',
        baseValue: 'US',
        items: [
          {
            key: 'US',
            text: 'US',
          },
          {
            key: 'APAC',
            text: 'APAC',
          },
          {
            key: 'EU',
            text: 'EU',
          },
        ],
        rename: 'Endpoint Region',
        renameDescription: (): string => {
          return 'Example: US or APAC or EU';
        },
        errorHandler: handleEmptyParamValueError('Endpoint Region is required.'),
      },
      number_of_ads: {
        rename: 'Number Of Ads',
        priority: 1001,
        type: 'enum',
        items: [
          {
            key: 1,
            text: '1',
          },
          {
            key: 2,
            text: '2',
          },
          {
            key: 3,
            text: '3',
          },
          {
            key: 4,
            text: '4',
          },
          {
            key: 5,
            text: '5',
          },
          {
            key: 6,
            text: '6',
          },
          {
            key: 7,
            text: '7',
          },
          {
            key: 8,
            text: '8',
          },
        ],
      },
      max_seq: {
        rename: 'Max Sequence',
        type: 'enum',
        items: [
          {
            key: 1,
            text: '1',
          },
          {
            key: 2,
            text: '2',
          },
          {
            key: 3,
            text: '3',
          },
          {
            key: 4,
            text: '4',
          },
          {
            key: 5,
            text: '5',
          },
          {
            key: 6,
            text: '6',
          },
          {
            key: 7,
            text: '7',
          },
          {
            key: 8,
            text: '8',
          },
        ],
      },
    },
    marketingarchitects: {
      publisherID: {
        rename: 'Publisher ID',
        errorHandler: handleEmptyParamValueError('Publisher ID is required.'),
      },
    },
    nativeVast: {
      adFormat: {
        rename: 'Ad Format',
        renameDescription: (): string => {
          return 'Default: Banner';
        },
        type: 'enum',
        baseValue: NATIVE_VAST_AD_FORMAT_BANNER,
        items: [
          {
            key: NATIVE_VAST_AD_FORMAT_BANNER,
            text: NATIVE_VAST_AD_FORMAT_BANNER,
          },
          {
            key: NATIVE_VAST_AD_FORMAT_CLICKABLE_VIDEO,
            text: NATIVE_VAST_AD_FORMAT_CLICKABLE_VIDEO,
          },
          {
            key: NATIVE_VAST_AD_FORMAT_BANNER_VIDEO_OVERLAY,
            text: NATIVE_VAST_AD_FORMAT_BANNER_VIDEO_OVERLAY,
          },
          {
            key: NATIVE_VAST_AD_FORMAT_COMPANION,
            text: NATIVE_VAST_AD_FORMAT_COMPANION,
          },
          {
            key: NATIVE_VAST_AD_FORMAT_HALO_BANNER,
            text: NATIVE_VAST_AD_FORMAT_HALO_BANNER,
          },
        ],
      },
    },
    premion: {
      publisherID: {
        rename: 'Publisher ID',
        errorHandler: handleEmptyParamValueError('Publisher ID is required.'),
      },
      publisherName: {
        rename: 'Publisher Name',
        errorHandler: handleEmptyParamValueError('Publisher Name is required.'),
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
      },
      endpointDomain: {
        type: 'enum',
        baseValue: '1',
        items: [
          {
            key: '1',
            text: 'US East',
          },
          {
            key: '2',
            text: 'US West',
          },
        ],
        rename: 'Endpoint Domain',
        renameDescription: (): string => {
          return '';
        },
        errorHandler: handleEmptyParamValueError('Endpoint Domain is required.'),
      },
    },
    rubicon: {
      appDomain: {
        rename: 'App Domain',
        onlyForSuperUser: true,
      },
      appStoreURL: {
        rename: 'App Store URL',
        onlyForSuperUser: true,
      },
      username: {
        rename: 'Username',
        hidden: true,
      },
      password: {
        rename: 'Password',
        hidden: true,
      },
      accountId: {
        rename: 'Account ID',
        renameDescription: (description: string): string => {
          return '(accountId) ' + description;
        },
      },
      siteId: {
        rename: 'Site ID',
        renameDescription: (description: string): string => {
          return '(siteId) ' + description;
        },
      },
      zoneId: {
        rename: 'Zone ID',
        renameDescription: (description: string): string => {
          return '(zoneId) ' + description;
        },
      },
      endpointDomain: {
        type: 'enum',
        baseValue: '0',
        items: [
          {
            key: '0',
            text: 'US',
          },
          {
            key: '1',
            text: 'APAC',
          },
        ],
        rename: 'Endpoint Domain',
        renameDescription: (): string => {
          return 'Example: US or APAC';
        },
        errorHandler: handleEmptyParamValueError('Endpoint Domain is required.'),
      },
    },
    sabio: {
      publisher_id: {
        rename: 'Publisher ID',
        renameDescription: (): string => {
          return 'publisher_id';
        },
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Precise acceptable durations for video creatives in seconds - comma separated integers';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Price floor for the dynamic portion of a video ad pod; a value of 0 indicates that this value will not be used';
        },
      },
    },
    samsungAdManager: {
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
      },
    },
    samsungDSP: {},
    samsungssp: {
      endpoint_override: {
        onlyForSuperUser: true,
        type: 'enum',
        items: [
          {
            key: '',
            text: 'Default endpoint (None)',
          },
          {
            key: 'staging',
            text: 'Staging endpoint',
          },
          {
            key: 'eu',
            text: 'EU endpoint',
          },
        ],
      },
    },
    smaato: {
      adSpace: {
        rename: 'Ad Space',
        renameDescription: (): string => {
          return 'adSpace';
        },
      },
      publisherID: {
        rename: 'Publisher ID',
        renameDescription: (): string => {
          return 'publisherID';
        },
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Precise acceptable durations for video creatives in seconds - comma separated integers';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
      },
    },
    rhythmone: {
      endpoint: {
        rename: 'Endpoint',
        type: 'enum',
        items: [
          {
            key: 'http://publica.east-s2s.1rx.io/s2s/publica',
            text: 'US East',
          },
          {
            key: 'http://publica.west-s2s.1rx.io/s2s/publica',
            text: 'US West',
          },
          {
            key: 'http://publica.ap-s2s.1rx.io/s2s/publica',
            text: 'APAC',
          },
          {
            key: 'http://publica.eu-s2s.1rx.io/s2s/publica',
            text: 'EU',
          },
        ],
      },
      site_id: {
        rename: 'Site ID',
        renameDescription: (): string => {
          return 'site_id';
        },
      },
      publisher_id: {
        rename: 'Publisher ID',
        renameDescription: (): string => {
          return 'publisher_id';
        },
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
      },
    },
    rokuHB: {
      index: {
        rename: 'Index',
      },
      subDomain: {
        rename: 'Sub Domain',
      },
      seatId: {
        rename: 'Seat ID',
      },
      tagId: {
        rename: 'Tag ID',
      },
    },
    rokuORTB: {
      subDomain: {
        rename: 'Sub Domain',
      },
      seatId: {
        rename: 'Seat ID',
      },
      tagId: {
        rename: 'Tag ID',
      },
      publisherId: {
        rename: 'Publisher ID',
      },
    },
    medianet: {
      region: {
        rename: 'Region',
        type: 'enum',
        items: [
          {
            key: 'us-east-1',
            text: 'us-east-1',
          },
          {
            key: 'eu-west-1',
            text: 'eu-west-1',
          },
        ],
      },
      publisher_id: {
        rename: 'Publisher ID',
        renameDescription: (): string => {
          return 'publisher_id';
        },
      },
      placement_id: {
        rename: 'Placement ID',
        renameDescription: (): string => {
          return 'placement_id';
        },
      },
    },
    nobid: {
      publisher_id: {
        rename: 'Publisher ID',
        renameDescription: (): string => {
          return 'publisher_id';
        },
      },
    },
    sharethrough: {
      placement_id: {
        rename: 'Placement ID',
        renameDescription: (): string => {
          return 'placement_id - Mandatory';
        },
        errorHandler: handleEmptyParamValueError('Placement ID is required.'),
      },
      publisher_id: {
        rename: 'Publisher ID',
        renameDescription: (): string => {
          return 'publisher_id - Mandatory';
        },
        errorHandler: handleEmptyParamValueError('Publisher ID is required.'),
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
      },
    },
    smartadserver: {
      region: {
        rename: 'Region',
        type: 'enum',
        items: [
          {
            key: 'apac-1',
            text: 'APAC',
          },
          {
            key: 'eu-west-1',
            text: 'Europe',
          },
          {
            key: 'us-east-1',
            text: 'US-East',
          },
          {
            key: 'us-west-1',
            text: 'US-West',
          },
        ],
      },
      siteid: {
        rename: 'Site ID',
        renameDescription: (): string => {
          return 'siteid';
        },
      },
      pgid: {
        rename: 'PG ID',
        renameDescription: (): string => {
          return 'pgid';
        },
      },
      fmtid: {
        rename: 'FMT ID',
        renameDescription: (): string => {
          return 'fmtid';
        },
      },
      publisher_id: {
        rename: 'Network ID',
        renameDescription: (): string => {
          return 'publisher_id';
        },
      },
    },
    smartclip: {
      publisher_id: {
        rename: 'Publisher ID',
        renameDescription: (): string => {
          return 'publisher_id - Mandatory';
        },
        errorHandler: handleEmptyParamValueError('Publisher ID is required.'),
      },
      site_id: {
        rename: 'Site ID',
        renameDescription: (): string => {
          return 'site_id';
        },
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Precise acceptable durations for video creatives in seconds - comma separated integers';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Price floor for the dynamic portion of a video ad pod; a value of 0 indicates that this value will not be used';
        },
      },
    },
    smartstream: {
      publisher_id: {
        rename: 'Publisher ID',
        errorHandler: handleEmptyParamValueError('Publisher ID is required.'),
      },
      publisher_name: {
        rename: 'Publisher Name',
        renameDescription: (): string => {
          return 'publisher_name';
        },
      },
      site_id: {
        rename: 'Site ID',
        renameDescription: (): string => {
          return 'site_id';
        },
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Precise acceptable durations for video creatives in seconds - comma separated integers';
        },
      },
    },
    smartyads: {
      publisherid: {
        rename: 'Publisher ID',
        renameDescription: (): string => {
          return 'publisherid';
        },
      },
    },
    indexExchange: {
      pid: {
        onlyForSuperUser: true,
        defaultValue: 190692,
      },
      endpoint: {
        rename: 'Endpoint Domain',
        renameDescription: (): string => {
          return 'Example: Default (US East) or APAC';
        },
        type: 'enum',
        baseValue: ENDPOINT_DEFAULT_KEY,
        items: [
          {
            key: ENDPOINT_DEFAULT_KEY,
            text: ENDPOINT_DEFAULT_KEY,
          },
          {
            key: ENDPOINT_EU_KEY,
            text: ENDPOINT_EU_KEY,
          },
          {
            key: ENDPOINT_APAC_KEY,
            text: ENDPOINT_APAC_KEY,
          },
          {
            key: ENDPOINT_US_WEST_KEY,
            text: 'US West',
          },
          {
            key: ENDPOINT_APAC_NORTH_KEY,
            text: ENDPOINT_APAC_NORTH_KEY,
          },
        ],
        itemsSuperOnly: [
          {
            key: ENDPOINT_STAGING_KEY, // Super-user only
            text: ENDPOINT_STAGING_KEY,
          },
          {
            key: ENDPOINT_PROD_KEY, // Super-user only
            text: ENDPOINT_PROD_KEY,
          },
        ],
      },
      siteID: {
        rename: 'Site ID',
        renameDescription: (): string => {
          return 'siteID';
        },
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Precise acceptable durations for video creatives in seconds - comma separated integers';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Price floor for the dynamic portion of a video ad pod; a value of 0 indicates that this value will not be used';
        },
      },
      publisher_name: {
        rename: 'Publisher Name',
        renameDescription: (): string => {
          return 'publisher_name';
        },
      },
    },
    elementaltv: {
      supply_id: {
        rename: 'Supply ID',
        errorHandler: handleEmptyParamValueError('Supply ID is required.'),
        renameDescription: (): string => {
          return 'supply_id';
        },
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Precise acceptable durations for video creatives in seconds - comma separated integers';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Price floor for the dynamic portion of a video ad pod; a value of 0 indicates that this value will not be used';
        },
      },
    },
    mobilefuse: {
      publisherID: {
        rename: 'Publisher ID',
        errorHandler: handleEmptyParamValueError('Publisher ID is required.'),
      },
      placementID: {
        rename: 'Placement ID',
        errorHandler: handleEmptyParamValueError('Placement ID is required.'),
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Precise acceptable durations for video creatives in seconds - comma separated integers';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Price floor for the dynamic portion of a video ad pod; a value of 0 indicates that this value will not be used';
        },
      },
    },
    motiv: {
      publisher_name: {
        rename: 'Publisher Name',
        errorHandler(v: string): string {
          if (!v) {
            return 'Publisher Name is required in the bidder parameters.';
          }
          if (v.includes(' ')) {
            return 'Publisher Name cannot contain spaces.';
          }
          return '';
        },
        renameDescription: (): string => {
          return 'The publisher name to be used in the bid request URL.';
        },
      },
    },
    openx: {
      endpoint: {
        rename: 'Endpoint',
        type: 'enum',
        baseValue: 'https://rtb-us-east1.openx.net/openrtb/publica',
        items: [
          {
            key: 'https://rtb-us-east1.openx.net/openrtb/publica',
            text: 'US-EAST (https://rtb-us-east1.openx.net/openrtb/publica)',
          },
          {
            key: 'https://rtb-us-west1.openx.net/openrtb/publica',
            text: 'US-WEST (https://rtb-us-west1.openx.net/openrtb/publica)',
          },
          {
            key: 'https://rtb-europe-west1.openx.net/openrtb/publica',
            text: 'EMEA (https://rtb-europe-west1.openx.net/openrtb/publica)',
          },
          {
            key: 'https://rtb-asia-southeast1.openx.net/openrtb/publica',
            text: 'APAC (https://rtb-asia-southeast1.openx.net/openrtb/publica)',
          },
          {
            key: 'https://rtb-asia-east1.openx.net/openrtb/publica',
            text: 'APAC-EAST (https://rtb-asia-east1.openx.net/openrtb/publica)',
          },
          {
            key: 'http://rtb-xv.openx.net/openrtb/publica',
            text: '(Legacy) US-EAST (http://rtb-xv.openx.net/openrtb/publica)',
          },
          {
            key: 'http://rtb-xa.openx.net/openrtb/publica',
            text: '(Legacy) EMEA (http://rtb-xa.openx.net/openrtb/publica)',
          },
        ],
      },
      publisherid: {
        rename: 'Publisher ID',
        renameDescription: (): string => {
          return 'publisherid';
        },
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Precise acceptable durations for video creatives in seconds - comma separated integers';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
      },
    },
    origin: {
      pub_id: {
        rename: 'Publisher ID',
        renameDescription: (): string => {
          return 'pub_id';
        },
      },
    },
    pubmatic: {
      publisherId: {
        rename: 'Publisher ID',
        renameDescription: (description: string): string => {
          return '(publisher_id) ' + description;
        },
      },
      adSlot: {
        rename: 'Ad Slot',
        renameDescription: (description: string): string => {
          return '(adSlot) ' + description;
        },
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Precise acceptable durations for video creatives in seconds - comma separated integers';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Price floor for the dynamic portion of a video ad pod; a value of 0 indicates that this value will not be used';
        },
      },
    },
    sonobi: {
      publisher_id: {
        rename: 'Publisher ID',
        renameDescription: (): string => {
          return 'publisher_id - Mandatory';
        },
      },
      placement_id: {
        rename: 'Placement ID',
        renameDescription: (): string => {
          return 'placement_id - Mandatory';
        },
      },
      user_id: {
        rename: 'User ID',
        renameDescription: (): string => {
          return 'user_id - Optional';
        },
      },
    },
    sovrn: {
      pid: {
        renameDescription: (): string => 'publisher_id - Mandatory',
        errorHandler: handleEmptyParamValueError('PID is required.'),
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
      },
      tagid: {
        rename: 'Tag ID',
        renameDescription: (): string => {
          return 'tag_id';
        },
      },
    },
    stroer: {
      publisher_id: {
        rename: 'Publisher ID',
        renameDescription: (): string => {
          return 'publisher_id - Mandatory';
        },
        errorHandler: handleEmptyParamValueError('Publisher ID is required.'),
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string =>
          'Precise acceptable durations for video creatives in seconds - comma separated integers',
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
        renameDescription: (): string =>
          'Price floor for the dynamic portion of a video ad pod; a value of 0 indicates that this value will not be used',
      },
    },
    tappx: {
      endpoint: {
        rename: 'Endpoint',
        renameDescription: (): string => {
          return 'endpoint';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
        renameDescription: (): string =>
          'Price floor for the dynamic portion of a video ad pod; a value of 0 indicates that this value will not be used',
      },
      mktag: {
        rename: 'Mktag',
        renameDescription: (): string => {
          return 'mktag';
        },
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string =>
          'Precise acceptable durations for video creatives in seconds - comma separated integers',
      },
      tapp_x_app_key: {
        rename: 'Tappx App Key',
        renameDescription: (): string => {
          return 'tapp_x_app_key';
        },
      },
    },
    teads: {
      placementid: {
        rename: 'Placement ID',
        renameDescription: (): string => {
          return 'placementid';
        },
      },
    },
    telaria: {
      endpointType: {
        rename: 'Endpoint Type',
        type: 'enum',
        items: [
          {
            key: 'ctv',
            text: 'CTV',
          },
          {
            key: 'web',
            text: 'Web',
          },
          {
            key: 'app',
            text: 'Mobile In-App',
          },
        ],
      },
      supplyCode: {
        rename: 'Supply Code',
        renameDescription: (description: string): string => {
          return '(supplyCode) ' + description;
        },
      },
      adCode: {
        rename: 'Ad Code',
        renameDescription: (description: string): string => {
          return '(adCode) ' + description;
        },
      },
    },
    thetradedesk: {
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Precise acceptable durations for video creatives in seconds - comma separated integers';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Price floor for the dynamic portion of a video ad pod; a value of 0 indicates that this value will not be used';
        },
      },
      publisher_name: {
        rename: 'Publisher Name',
        errorHandler(v: string): string {
          if (!v || v === '') {
            return 'Publisher name is required in the bidder parameters.';
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Publisher name to use in the bid request URL';
        },
      },
      endpoint_subdomain: {
        rename: 'Endpoint Subdomain',
        errorHandler(v: string): string {
          if (!v || v === '') {
            return 'Endpoint Subdomain is required in the bidder parameters.';
          }
          return '';
        },
        renameDescription: (): string => {
          return 'The endpoint subdomain to prepend the bid request URL.';
        },
      },
    },
    theviewpoint: {
      account_id: {
        rename: 'Account ID',
        renameDescription: (): string => {
          return 'account_id';
        },
      },
      placement_id: {
        rename: 'Placement ID',
        renameDescription: (): string => {
          return 'placement_id';
        },
      },
    },
    thin: {
      publisherId: {
        rename: 'Publisher ID',
        renameDescription: (description: string): string => {
          return '(publisher_id) ' + description;
        },
      },
      adSlot: {
        rename: 'Ad Slot',
        renameDescription: (description: string): string => {
          return '(adSlot) ' + description;
        },
      },
      bidTimeoutRate: {
        rename: 'Bid Timeout Rate',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const bidTimeoutRate = parseFloat(v);
          if (Number.isNaN(bidTimeoutRate)) {
            return 'Bid Timeout Rate must be a number';
          }
          if (!Number.isInteger(bidTimeoutRate)) {
            return 'Bid Timeout Rate must be an integer';
          }
          if (bidTimeoutRate > 100) {
            return 'The Bid Timeout Rate must be less than or equal to 100';
          }
          if (bidTimeoutRate < 0) {
            return 'The Bid Timeout Rate must be greater than or equal to 0';
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Bid Timeout Rate to force the LoadTest bidder to timeout a request.';
        },
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Precise acceptable durations for video creatives in seconds - comma separated integers';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
        renameDescription: (): string => {
          return 'Price floor for the dynamic portion of a video ad pod; a value of 0 indicates that this value will not be used';
        },
      },
    },
    triplelift: {
      inventory_code: {
        rename: 'Inventory Code (Placement Name)',
        //   renameDescription: (): string => {
        //     return 'inventory_code - Mandatory';
        //   },
        //   errorHandler(v: string): string {
        //     if (!v || v === '') {
        //       return 'Inventory code is required.';
        //     }
        //     return '';
        //   },
        // },
      },
      publisher_id: {
        rename: 'Publisher ID',
        renameDescription: (): string => {
          return 'publisher_id - Optional';
        },
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
      },
    },
    trustx: {
      tagid: {
        rename: 'Tag ID',
        renameDescription: (): string => {
          return 'tag_id';
        },
      },
    },
    undertone: {
      publisherID: {
        rename: 'Publisher ID',
        errorHandler: handleEmptyParamValueError('Publisher ID is required.'),
      },
      placementID: {
        rename: 'Placement ID',
        errorHandler: handleEmptyParamValueError('Placement ID is required.'),
      },
    },
    publica: {
      pubId: {
        rename: 'Publisher ID',
        defaultValue: '1',
        onlyForSuperUser: true,
      },
      creativeId: {
        rename: 'Creative ID',
        defaultValue: '1',
        onlyForSuperUser: true,
      },
      price: {
        rename: 'Price',
        errorHandler(v: string): string {
          if (!!v && v.indexOf(',') > -1) {
            return 'Please use . instead of , for decimal';
          } else if (!!v && isNaN(parseFloat(v))) {
            return 'Please input a valid decimal';
          }
          return '';
        },
      },
      currency: {
        rename: 'Currency',
        onlyForSuperUser: true,
      },
    },
    publicaCampaign: {
      vastURL: {
        rename: 'Campaign',
        renameDescription: (description: string): string => {
          return description.replace('Publica', getResellerTitle());
        },
        type: 'enum',
        getItems: (_: string, campaigns: IAdserverCampaign[]): IKeyValPair[] => {
          return [
            {
              text: 'All',
              key: 'undefined', // all campaign means no 'campaign_id' parameter in the vastURL, so an 'undefined' campaign id - see the getCampaignIDFromVastURL func in bidder-settings-form-view.tsx
            },
          ].concat(
            campaigns.map((c: IAdserverCampaign): IKeyValPair => {
              return {
                text: `${c.Name} [${c.ID}]`,
                key: `${c.ID}`,
              };
            }),
          );
        },
      },
    },
    pulsepoint: {
      endpoint_id: {
        rename: 'Endpoint ID',
        errorHandler(v: string): string {
          if (v.includes(' ')) {
            return 'Endpoint ID may not contain spaces';
          }
          return '';
        },
      },
      endpoint_region: {
        rename: 'Endpoint Region',
        errorHandler(v: string): string {
          if (v.includes(' ')) {
            return 'Endpoint Region may not contain spaces';
          }
          return '';
        },
      },
    },
    // now Yahoo, keep for existing verizon params
    verizon: {
      appSiteID: {
        rename: 'App Site ID',
      },
      publisherID: {
        rename: 'Publisher ID',
      },
      placementID: {
        rename: 'Placement ID',
      },
      endpoint: {
        rename: 'Endpoint Region',
        type: 'enum',
        baseValue: 'global',
        items: [
          {
            key: 'global',
            text: 'Global (default)',
          },
          {
            key: 'us',
            text: 'US',
          },
          {
            key: 'eu',
            text: 'EU',
          },
          {
            key: 'ap',
            text: 'AP',
          },
        ],
      },
    },
    videobyte: {
      publisher_id: {
        rename: 'Publisher ID',
        renameDescription: (): string => {
          return 'publisher_id';
        },
      },
      placement_id: {
        rename: 'Placement ID',
        renameDescription: (): string => {
          return 'placement_id';
        },
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
      },
    },
    videoHeroesRevShare: {
      globalTimeoutOffset: {
        onlyForSuperUser: true,
      },
      siteID: {
        rename: 'Channel ID',
        renameDescription: (description: string): string => {
          return '(siteId) ' + description;
        },
      },
      platformMarketplacePublisher: {
        rename: 'Managed Platform Connections Publisher',
        type: 'enum',
      },
    },
    videoHeroesFixedCPM: {
      globalTimeoutOffset: {
        onlyForSuperUser: true,
      },
      siteID: {
        rename: 'Channel ID',
        renameDescription: (description: string): string => {
          return '(siteId) ' + description;
        },
      },
      platformMarketplacePublisher: {
        rename: 'Managed Platform Connections Publisher',
        type: 'enum',
      },
    },
    vrtcal: {
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
        renameDescription: (): string =>
          'Precise acceptable durations for video creatives in seconds - comma separated integers',
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
        renameDescription: (): string =>
          'Price floor for the dynamic portion of a video ad pod; a value of 0 indicates that this value will not be used',
      },
    },
    wurl: {
      use_test_endpoint: {
        rename: 'Use Test Endpoint',
        onlyForSuperUser: true,
        priority: 1000,
        type: 'switch',
        trueValue: 'true',
        falseValue: 'false',
      },
      partner_id: {
        rename: 'Partner ID',
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
      },
    },
    yieldmo: {
      placementID: {
        rename: 'Placement ID',
      },
      renameDescription: (): string => {
        return 'placement_id - Mandatory';
      },
      errorHandler: handleEmptyParamValueError('Placement ID is required.'),
    },
    zedo: {
      publisherID: {
        rename: 'Publisher ID',
        errorHandler: handleEmptyParamValueError('Publisher ID is required.'),
        renameDescription: (): string => {
          return 'publisherID';
        },
      },
    },
    nexstar: {
      // VGI CTV
      endpoint: {
        rename: 'Use Smaato Pipeline',
        onlyForSuperUser: true,
        priority: 1000,
        type: 'switch',
        trueValue: 'smaato',
        falseValue: 'nexstar',
      },
      adSpace: {
        rename: 'Ad Space',
        renameDescription: (): string => {
          return 'adSpace: for Smaato pipeline';
        },
      },
      publisherID: {
        rename: 'Publisher ID',
        renameDescription: (): string => {
          return 'publisherID: for Smaato pipeline';
        },
      },
      required_duration: {
        rename: 'Required Duration',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const durations = v.split(',');
          for (const dur of durations) {
            const duration = parseInt(dur, 10);
            if (duration === 0 || Number.isNaN(duration) || duration < 0) {
              return 'Each duration must be a number greater than 0';
            }
          }
          return '';
        },
      },
      minimum_cpm_per_second: {
        rename: 'Minimum CPM Per Second',
        errorHandler(v: string): string {
          if (v === '') {
            return '';
          }
          const minCPM = parseFloat(v);
          if (Number.isNaN(minCPM)) {
            return 'Minimum CPM must be a number greater than 0; a value of 0 indicates that this value will not be used';
          }
          return '';
        },
      },
    },
    vibe: {
      placementId: {
        rename: 'Placement ID',
        renameDescription: (): string => {
          return 'placementId';
        },
      },
      publisherID: {
        rename: 'Publisher ID',
        errorHandler: handleEmptyParamValueError('Publisher ID is required.'),
        renameDescription: (): string => {
          return 'publisherID';
        },
      },
    },
    nhnace: {
      publisher_name: {
        rename: 'Publisher Name',
        errorHandler: handleEmptyParamValueError('Publisher Name is required.'),
        renameDescription: (): string => {
          return 'publisherName';
        },
      },
    },
    mezzo: {
      publisher_id: {
        rename: 'Publisher ID',
        errorHandler: handleEmptyParamValueError('Publisher ID is required.'),
        renameDescription: (): string => {
          return 'publisherID';
        },
      },
    },
    motiv: {},
    epsilon: {},
    smart: {},
    apsGuarantee: {
      inv: {
        rename: 'Inv',
      },
    },
    apsOpenAuction: {},
    apsPMP: {},
    samsungssptest: {},
  };
};

export function hasParamOption(bidder: string, name: string, attr: string): boolean {
  const HeaderBidderParamsUIconfig = getHeaderBidderParamsUIConfig();
  return (
    bidder in HeaderBidderParamsUIconfig &&
    name in HeaderBidderParamsUIconfig[bidder] &&
    attr in HeaderBidderParamsUIconfig[bidder][name]
  );
}

export function getParamOption<T>(bidder: string, name: string, attr: string): T {
  const HeaderBidderParamsUIconfig = getHeaderBidderParamsUIConfig();
  return HeaderBidderParamsUIconfig[bidder][name][attr] as T;
}

export function shouldParamOptionOverwritten(
  bidder: string,
  name: string,
  attr: string,
  expected?: any,
): boolean {
  const hasAttr = hasParamOption(bidder, name, attr);
  if (hasAttr) {
    const HeaderBidderParamsUIconfig = getHeaderBidderParamsUIConfig();
    return expected === undefined
      ? hasAttr
      : HeaderBidderParamsUIconfig[bidder][name][attr] === expected;
  }
  return false;
}

export function sortParamOptions(a: IHeaderBidderParamOption, b: IHeaderBidderParamOption): number {
  // higher priority should come first
  const priorityA =
    (hasParamOption(a.Bidder, a.Name, 'priority') &&
      getParamOption<number>(a.Bidder, a.Name, 'priority')) ||
    0;
  const priorityB =
    (hasParamOption(b.Bidder, b.Name, 'priority') &&
      getParamOption<number>(b.Bidder, b.Name, 'priority')) ||
    0;
  if (priorityA < priorityB) {
    return 1;
  }
  if (priorityA > priorityB) {
    return -1;
  }
  return 0;
}

// Check if the given string matches a known bidder type. Ex: 'spotx'.
// Case insensitive.
export function isStringKnownBidderType(str: string, publisher: IPublisher): boolean {
  // Ignore empty strings.
  if (!str) {
    return false;
  }
  const headerBidderAdapters = getHeaderBidderAdaptersForPublisher('', false, publisher);

  // Check against the list of known bidder types.
  for (let i = 0; i < headerBidderAdapters.length; i++) {
    switch (str.toLowerCase()) {
      case headerBidderAdapters[i].key.toLowerCase():
      case headerBidderAdapters[i].text.toLowerCase():
        // Str is a known bidder type.
        return true;
    }
  }

  // Str is not a known bidder type.
  return false;
}

// Check if the given string matches a known bidder type. Ex: 'spotx'.
// Case insensitive.
export function getParamDefaultValue(name: string): any {
  switch (name) {
    case 'globalTimeoutOffset':
      return '150';
    case 'pid':
      return '190692';
    default:
      return '';
  }
}
